import moment from 'moment';

import { getReportsAPIConfig } from '../../../shared/api-configs/reports';
import { API_DATE_FORMAT } from '../../../shared/utils/date';

const EntityTypeToFieldName = {
    account: 'account_id',
    client: 'client_id',
};

const createMetricsReportDefinition = ({
    entityType,
    entityId,
    name,
    startDate,
    endDate,
}) => ({
    type: 'metrics',
    name,
    date_range: {
        start: startDate,
        end: endDate,
    },
    fields: ['metric', 'total'],
    predicates: [
        {
            field: EntityTypeToFieldName[entityType],
            operator: 'equals',
            values: [entityId],
        },
        {
            field: 'metric',
            operator: 'in',
            values: ['cost', 'conversions', 'cost_per_conversion'],
        },
    ],
});

const createLeaderBoardReportDefinition = ({
    name,
    startDate,
    endDate,
}) => ({
    type: 'leaderboard',
    name,
    date_range: {
        start: startDate,
        end: endDate,
    },
    fields: ['name', 'quantity'],
});

const createAggregateAppliedEconomiesReportDefinition = ({
    entityType,
    entityId,
    name,
    startDate,
    endDate,
}) => ({
    type: 'aggregated_applied_economies',
    name,
    date_range: {
        start: startDate,
        end: endDate,
    },
    fields: ['metric', 'total'],
    predicates: [
        {
            field: EntityTypeToFieldName[entityType],
            operator: 'equals',
            values: [entityId],
        },
    ],
});

const createAggregatePotentialSavingsReportDefinition = ({
    entityType,
    entityId,
    name,
    startDate,
    endDate,
}) => ({
    type: 'aggregated_potential_savings',
    name,
    date_range: {
        start: startDate,
        end: endDate,
    },
    fields: ['metric', 'total'],
    predicates: [
        {
            field: EntityTypeToFieldName[entityType],
            operator: 'equals',
            values: [entityId],
        },
    ],
});

const getThisMonthMetricsReport = ({ entityType, entityId }) => createMetricsReportDefinition({
    name: 'THIS_MONTH_METRICS',
    startDate: moment().startOf('month').format(API_DATE_FORMAT),
    endDate: moment().format(API_DATE_FORMAT),
    entityType,
    entityId,
});

const getThisDayLeaderBoardReport = () => createLeaderBoardReportDefinition({
    name: 'THIS_DAY_LEADER_BOARD',
    startDate: moment().format(API_DATE_FORMAT),
    endDate: moment().format(API_DATE_FORMAT),
});

const getThisWeekLeaderBoardReport = () => createLeaderBoardReportDefinition({
    name: 'THIS_WEEK_LEADER_BOARD',
    startDate: moment().startOf('week').format(API_DATE_FORMAT),
    endDate: moment().format(API_DATE_FORMAT),
});

const getThisMonthLeaderBoardReport = () => createLeaderBoardReportDefinition({
    name: 'THIS_MONTH_LEADER_BOARD',
    startDate: moment().startOf('month').format(API_DATE_FORMAT),
    endDate: moment().format(API_DATE_FORMAT),
});

const getThisQuarterLeaderBoardReport = () => createLeaderBoardReportDefinition({
    name: 'THIS_QUARTER_LEADER_BOARD',
    startDate: moment().startOf('quarter').format(API_DATE_FORMAT),
    endDate: moment().format(API_DATE_FORMAT),
});

const getThisYearAggregateAppliedEconomicsReport = ({ entityType, entityId }) => (
    createAggregateAppliedEconomiesReportDefinition({
        entityType,
        entityId,
        name: 'THIS_YEAR_APPLIED_SAVINGS',
        startDate: moment().startOf('year').format(API_DATE_FORMAT),
        endDate: moment().format(API_DATE_FORMAT),
    })
);

const getLast12MonthsAggregateAppliedEconomicsReport = ({ entityType, entityId }) => (
    createAggregateAppliedEconomiesReportDefinition({
        entityType,
        entityId,
        name: 'LAST_12_MONTHS_APPLIED_SAVINGS',
        startDate: moment().subtract(1, 'year').format(API_DATE_FORMAT),
        endDate: moment().format(API_DATE_FORMAT),
    })
);

const getThisDayAggregatePotentialSavingsReport = ({ entityType, entityId }) => (
    createAggregatePotentialSavingsReportDefinition({
        entityType,
        entityId,
        name: 'THIS_DAY_POTENTIAL_SAVINGS',
        startDate: moment().format(API_DATE_FORMAT),
        endDate: moment().format(API_DATE_FORMAT),
    })
);

const getGamificationAccountEconomiesReport = ({ entityType, segment }) => ({
    type: 'gamification_economies',
    name: 'ALL_TIME_GAMIFICATION_ECONOMIES',
    segment,
    fields: ['economyType', 'economyValue'],
    predicates: [
        {
            field: 'level',
            operator: 'equals',
            values: [entityType],
        },
    ],
});

const getDefaultReports = () => [
    getThisDayLeaderBoardReport(),
    getThisWeekLeaderBoardReport(),
    getThisMonthLeaderBoardReport(),
    getThisQuarterLeaderBoardReport(),
];

const getEntityReports = ({ entityType, entityId }) => [
    getGamificationAccountEconomiesReport({ entityType, segment: 'all' }),
    getThisMonthMetricsReport({ entityType, entityId }),
    ...getDefaultReports(),
    getThisYearAggregateAppliedEconomicsReport({ entityType, entityId }),
    getLast12MonthsAggregateAppliedEconomicsReport({ entityType, entityId }),
    getThisDayAggregatePotentialSavingsReport({ entityType, entityId }),
];

export const getInteractiveHeaderReportsAPIConfig = ({ entityType, entityId }) => {
    const isAllEntities = entityId === 'all';
    const reports = isAllEntities
        ? getDefaultReports()
        : getEntityReports({ entityType, entityId });

    return reports
        .filter(Boolean)
        .map(report => getReportsAPIConfig({ reports: [report] }));
};
